import React, { useMemo, useState } from 'react';
import { Box, Button, ButtonGroup, CircularProgress, Popper, TextField, Typography } from '@mui/material';
import { useGetAllCustomersStructureQuery, useGetFolderStructureQuery } from '../../../../../services/graphApi';
import { FolderItem } from '../../../../../types/graph';
import { CustomerFolderStructure } from '../FolderStructure/CustomerFolderStructure';
import { FormattedMessage } from 'react-intl';

interface CustomerTreeViewProps {
	onCustomerSelect: (customerName: string) => void;
	onFileSelect?: (file: FolderItem) => void;
	anchorEl?: HTMLElement | null;
	onClose?: () => void;
}

export const CustomerTreeView: React.FC<CustomerTreeViewProps> = ({ onCustomerSelect, onFileSelect, anchorEl }) => {
	const [filter, setFilter] = useState('');
	const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null);
	const [selectedLetter, setSelectedLetter] = useState<string | null>('A');

	const { data: customers, isLoading: isCustomersLoading, error: customersError } = useGetAllCustomersStructureQuery();
	const {
		data: folderStructure,
		isLoading: isFolderLoading,
		isFetching: isFolderFetching,
	} = useGetFolderStructureQuery({ customerName: selectedCustomer || '' }, { skip: !selectedCustomer });

	// Generate alphabet buttons
	const alphabet = '#ABCDEFGHIJKLMNOPQRSTUVWXYZÅÄÖ*'.split('');

	// Filter customers based on search text and selected letter
	const filteredCustomers = useMemo(() => {
		if (!customers?.children) return [];

		return customers.children.filter((customer: any) => {
			const name = customer.name.toLowerCase();
			const searchMatch = name.includes(filter.toLowerCase());

			// If no letter is selected or asterisk is selected, just use text search
			if (selectedLetter === null || selectedLetter === '*') return searchMatch;

			// If a letter is selected, check if it matches the letter filter
			const matchesLetter = selectedLetter === '#' ? !/^[a-zåäö]/i.test(name) : name.startsWith(selectedLetter.toLowerCase());

			// Return true if it matches both text search and letter filter
			return searchMatch && matchesLetter;
		});
	}, [customers?.children, filter, selectedLetter]);

	const handleCustomerClick = (customerName: string) => {
		setSelectedCustomer(prevCustomer => (prevCustomer === customerName ? null : customerName));
		onCustomerSelect(customerName);
	};

	const handleLetterClick = (letter: string) => {
		setSelectedLetter(prevLetter => (prevLetter === letter ? null : letter));
		setSelectedCustomer(null);
	};

	const content = (
		<Box sx={{ p: 2 }}>
			<Box sx={{ mb: 2 }}>
				<ButtonGroup
					variant='outlined'
					size='small'
					sx={{
						flexWrap: 'wrap',
						'& .MuiButtonGroup-grouped': {
							minWidth: '32px',
						},
					}}
				>
					{alphabet.map(letter => (
						<Button
							key={letter}
							onClick={() => handleLetterClick(letter)}
							variant={selectedLetter === letter ? 'contained' : 'outlined'}
						>
							{letter}
						</Button>
					))}
				</ButtonGroup>
			</Box>
			<TextField
				fullWidth
				label={<FormattedMessage id='customerTreeView.searchLabel' />}
				variant='standard'
				value={filter}
				onChange={e => {
					setFilter(e.target.value);
					setSelectedCustomer(null);
				}}
				sx={{ mb: 2 }}
			/>
			<Box sx={{ mb: 1 }}>
				<Typography variant='body2' color='text.secondary'>
					<FormattedMessage id='customerTreeView.customersFound' values={{ count: filteredCustomers.length }} />
				</Typography>
			</Box>
			<Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
				{filteredCustomers.map(customer => (
					<Box key={customer.name}>
						<Box
							onClick={() => handleCustomerClick(customer.name)}
							sx={{
								p: 1,
								borderRadius: 1,
								cursor: 'pointer',
								'&:hover': {
									bgcolor: 'action.hover',
								},
								...(selectedCustomer === customer.name && {
									bgcolor: 'action.selected',
								}),
							}}
						>
							<Typography>{customer.name}</Typography>
						</Box>
						{selectedCustomer === customer.name && (
							<Box sx={{ ml: 3, borderColor: 'divider', pl: 2 }}>
								{isFolderLoading || isFolderFetching ? (
									<Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
										<CircularProgress size={24} />
									</Box>
								) : (
									folderStructure && <CustomerFolderStructure item={folderStructure} onFileSelect={onFileSelect} />
								)}
							</Box>
						)}
					</Box>
				))}
			</Box>
		</Box>
	);

	return (
		<Popper
			open={Boolean(anchorEl)}
			anchorEl={anchorEl}
			placement='bottom-start'
			sx={{
				zIndex: 99999,
				width: anchorEl ? Math.max(anchorEl.getBoundingClientRect().width, 800) : '45rem',
				minWidth: '300px',
				maxWidth: '90vw',
			}}
		>
			<Box
				sx={{
					border: 1,
					borderColor: 'divider',
					bgcolor: 'background.paper',
					boxShadow: 3,
					borderRadius: 1,
					mt: 1,
					maxHeight: '80vh',
					overflow: 'auto',
				}}
			>
				{content}
			</Box>
		</Popper>
	);
};
